import { AppModule } from "@/store/modules/app";
import { ITrackCross } from "@/types/map";
import { loadModules } from "esri-loader";
import { checkPoints } from "../../MapCenterCheck/arcgis/CheckPoints";

export default class TrackInCross {
  private static intances: Map<string, TrackInCross>;

  public static getInstance(view: __esri.MapView) {
    const id = view.container.id;
    if (!TrackInCross.intances) {
      TrackInCross.intances = new Map();
    }
    let instance = TrackInCross.intances.get(id);
    if (!instance) {
      instance = new TrackInCross(view);
      TrackInCross.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.MapView;

  private initialized = false;
  private roadLayer!: __esri.FeatureLayer;
  private trackLayer!: __esri.FeatureLayer;
  private Graphic!: __esri.GraphicConstructor;

  private crossGraphics: Array<__esri.Graphic> = [];
  private trackGraphics: Array<__esri.Graphic> = [];

  constructor(view: __esri.MapView) {
    this.view = view;
  }

  private async initialize() {
    type MapModules = [
      typeof import("esri/Graphic"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/geometry/support/webMercatorUtils")
    ];
    const [Graphic, FeatureLayer, webMercatorUtils] = await (loadModules([
      "esri/Graphic",
      "esri/layers/FeatureLayer",
      "esri/geometry/support/webMercatorUtils",
    ]) as Promise<MapModules>);
    this.Graphic = Graphic;

    const url = `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/Unicron/ShangHai_Road/MapServer/8`;
    this.roadLayer = new FeatureLayer({
      url,
    });

    this.trackLayer = new FeatureLayer({
      source: [],
      objectIdField: "OBJECTID",
      geometryType: "polyline",
      fields: [
        {
          name: "OBJECTID",
          type: "oid",
        },
        {
          name: "routeState",
          type: "string",
        },
      ],
      renderer: {
        type: "unique-value",
        field: "routeState",
        defaultSymbol: {
          type: "simple-line",
          style: "short-dash",
          color: "orange",
          width: 6,
        },
        uniqueValueInfos: [
          // 高亮路径带箭头
          {
            value: "normal",
            symbol: {
              type: "cim",
              data: {
                type: "CIMSymbolReference",
                symbol: {
                  type: "CIMLineSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidStroke",
                      enable: true,
                      width: 8,
                      color: [0, 189, 44, 255],
                    },
                    {
                      type: "CIMVectorMarker",
                      enable: true,
                      size: 6,
                      markerPlacement: {
                        type: "CIMMarkerPlacementAlongLineSameSize",
                        endings: "WithMarkers",
                        placementTemplate: [20],
                      },
                      frame: {
                        xmin: -6,
                        ymin: -6,
                        xmax: 6,
                        ymax: 6,
                      },
                      markerGraphics: [
                        {
                          type: "CIMMarkerGraphic",
                          geometry: {
                            paths: [
                              [
                                [-8, 5],
                                [0, 0],
                                [-8, -5],
                              ],
                            ],
                          },
                          symbol: {
                            type: "CIMLineSymbol",
                            symbolLayers: [
                              {
                                type: "CIMSolidStroke",
                                enable: true,
                                color: [255, 255, 255, 255],
                                width: 2,
                              },
                            ],
                          },
                        },
                      ],
                    },
                  ],
                },
              },
            },
          },
        ],
      } as any,
    });
    this.view.map.add(this.trackLayer);

    this.initialized = true;
  }

  public async showCrossTrack(crosses: Array<ITrackCross>): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }

    let lastState: string = "";
    let path: Array<Array<number>> = [];

    // 显示路口和标注
    crosses.forEach((cross, index) => {
      const id: string = cross.id;
      let symbol: any;
      let yoffset: number;

      if (index === 0 || index === crosses.length - 1) {
        yoffset = 17;
        if (cross.delay) {
          symbol = {
            type: "picture-marker",
            url: "assets\\mapIcons\\pyramid-red-big.png",
            width: "26px",
            height: "34px",
            yoffset: yoffset + "px",
          } as any;
        } else {
          symbol = {
            type: "picture-marker",
            url: "assets\\mapIcons\\pyramid-blue-big.png",
            width: "26px",
            height: "34px",
            yoffset: yoffset + "px",
          } as any;
        }
      } else {
        yoffset = 8;
        if (cross.delay) {
          symbol = {
            type: "picture-marker",
            url: "assets\\mapIcons\\pyramid-red-small.png",
            width: "10px",
            height: "16px",
            yoffset: yoffset + "px",
          } as any;
        } else {
          symbol = {
            type: "picture-marker",
            url: "assets\\mapIcons\\pyramid-blue-small.png",
            width: "10px",
            height: "16px",
            yoffset: yoffset + "px",
          } as any;
        }
      }

      const checkPoint = checkPoints.find((cross) => cross.id === id);
      if (checkPoint) {
        const crossGraphic = new this.Graphic({
          geometry: {
            type: "point",
            x: checkPoint.position[0],
            y: checkPoint.position[1],
          } as any,
          symbol,
          attributes: {
            id,
          },
        });
        this.crossGraphics.push(crossGraphic);

        let labelText = checkPoint.name;
        yoffset += 20;
        if (cross.delay) {
          labelText += "\n延误 " + cross.delay + "分钟";
          yoffset += 20;
        }
        if (cross.phaseDirection) {
          labelText += "\n方向 " + cross.phaseDirection;
          yoffset += 20;
        }
        const labelGraphic = new this.Graphic({
          geometry: {
            type: "point",
            x: checkPoint.position[0],
            y: checkPoint.position[1],
          } as any,
          symbol: {
            type: "text",
            color: cross.delay ? "orange" : "lightblue",
            haloSize: 1,
            haloColor: "black",
            text: labelText,
            yoffset: yoffset + "px",
            font: {
              size: 12,
            },
          } as any,
        });
        this.crossGraphics.push(labelGraphic);
      }
    });
    this.view.graphics.addMany(this.crossGraphics);

    // 显示路径
    for (let i = 1; i < crosses.length; i++) {
      const id1 = crosses[i - 1].id;
      const id2 = crosses[i].id;

      const query = this.roadLayer.createQuery();
      query.where = `(P_FNode='${id1}' and P_TNode='${id2}') or (N_FNode='${id1}' and N_TNode='${id2}')`;
      const result = (await this.roadLayer.queryFeatures(query)).features;
      if (result.length > 0) {
        // 根据道路状态，连续相同状态的道路合并成一个graphic
        const roadGraphic = result[0];
        let subPath = (roadGraphic.geometry as __esri.Polyline).paths[0];

        // 如果路径是反向，将坐标集倒序
        if (roadGraphic.attributes["N_FNode"] === id1) {
          subPath = subPath.reverse();
        }

        if (i === 1) {
          path = subPath;
        } else {
          if (lastState === crosses[i].routeState) {
            path = path.concat(subPath);
          } else {
            const subGraphic = new this.Graphic({
              geometry: {
                type: "polyline",
                paths: [path],
              } as any,
              attributes: {
                routeState: lastState,
              },
            });
            this.trackGraphics.push(subGraphic);

            path = subPath;
          }

          if (i === crosses.length - 1) {
            const subGraphic = new this.Graphic({
              geometry: {
                type: "polyline",
                paths: [path],
              } as any,
              attributes: {
                routeState: crosses[i].routeState,
              },
            });
            this.trackGraphics.push(subGraphic);
          }
        }
        lastState = crosses[i].routeState || "normal";
      }
    }
    await this.trackLayer.applyEdits({ addFeatures: this.trackGraphics });
    await this.view.goTo(this.trackGraphics);
  }

  public async hideCrossTrack() {
    this.view.graphics.removeMany(this.crossGraphics);
    this.crossGraphics = [];

    await this.trackLayer.applyEdits({ deleteFeatures: this.trackGraphics });
    this.trackGraphics = [];
  }

  public highlightCross(crossId: string) {
    for (let graphic of this.crossGraphics) {
      if (graphic.attributes?.id === crossId) {
        const highlightGraphic = new this.Graphic({
          geometry: graphic.geometry,
          symbol: {
            type: "picture-marker",
            url: "assets/image/highlight.png",
            width: "60px",
            height: "60px",
          } as any,
        });
        this.view.graphics.add(highlightGraphic);

        setTimeout(() => {
          this.view.graphics.remove(highlightGraphic);
        }, 3000);

        break;
      }
    }
  }
}
